/**
 * Account address and target device selection of the deferred send.
 *
 * The "address" that the recipient shares is their account capability id with
 * a readable prefix (`ENVAC1-…`), in the line of the `ENVR1-` of the P2P
 * transfer code.
 *
 * Since the sender does not know on WHICH device the recipient will pick up, the send
 * plan does fan-out: one envelope per (file × selected device),
 * each encrypted for THAT device's key — per-device E2E.
 */

export const ACCOUNT_ADDRESS_PREFIX = "ENVAC1-";
const ACCOUNT_ID_RE = /^[0-9a-f]{32}$/;

export function buildAccountAddress(accountId: string): string {
  return `${ACCOUNT_ADDRESS_PREFIX}${accountId}`;
}

/**
 * Normalizes a typed/pasted address (spaces, uppercase, with or without
 * prefix) to the account id, or `null` if it is not plausible.
 */
export function parseAccountAddress(raw: string): string | null {
  let text = raw.trim().replaceAll(/\s+/g, "");
  const prefixLen = ACCOUNT_ADDRESS_PREFIX.length;
  if (text.slice(0, prefixLen).toUpperCase() === ACCOUNT_ADDRESS_PREFIX) {
    text = text.slice(prefixLen);
  }
  const id = text.toLowerCase();
  return ACCOUNT_ID_RE.test(id) ? id : null;
}

/** TRUSTED device of the RECIPIENT as published by the mailbox. */
export interface RemoteDevice {
  deviceId: string;
  name: string;
  encPub: string;
  addedAtUnix: number;
}

/**
 * Selects the target devices: a specific one if the sender chose it
 * (`[]` if it no longer exists — e.g. revoked between lookup and send), or ALL
 * (deduplicated, most recent first) so the recipient can pick up
 * from any of them.
 */
export function selectRecipientDevices(
  devices: RemoteDevice[],
  options?: { preferredDeviceId?: string },
): RemoteDevice[] {
  const seen = new Set<string>();
  const unique = devices.filter((d) => {
    if (seen.has(d.deviceId)) {
      return false;
    }
    seen.add(d.deviceId);
    return true;
  });
  const preferred = options?.preferredDeviceId;
  if (preferred !== undefined) {
    const match = unique.find((d) => d.deviceId === preferred);
    return match ? [match] : [];
  }
  return unique.toSorted(
    (a, b) => b.addedAtUnix - a.addedAtUnix || a.deviceId.localeCompare(b.deviceId),
  );
}

/** Deferred file limit (the mailbox is not cloud storage). */
export const MAX_DEFERRED_FILE_BYTES = 4 * 1024 * 1024;
/** Cap of envelopes per operation (fan-out files × devices bounded). */
export const MAX_DEFERRED_ENVELOPES = 16;

export type DeferredPlan =
  | { kind: "ok"; sends: { fileIndex: number; device: RemoteDevice }[] }
  | { kind: "no-devices" }
  | { kind: "no-files" }
  | { kind: "file-too-large"; fileName: string }
  | { kind: "too-many-envelopes"; count: number };

/**
 * Deferred send plan: validates sizes and bounds the fan-out BEFORE encrypting
 * anything (encrypting N large envelopes only to fail later would be wasted work).
 */
export function planDeferredSends(
  files: { name: string; size: number }[],
  devices: RemoteDevice[],
): DeferredPlan {
  if (devices.length === 0) {
    return { kind: "no-devices" };
  }
  if (files.length === 0) {
    return { kind: "no-files" };
  }
  for (const file of files) {
    if (file.size > MAX_DEFERRED_FILE_BYTES) {
      return { kind: "file-too-large", fileName: file.name };
    }
  }
  const count = files.length * devices.length;
  if (count > MAX_DEFERRED_ENVELOPES) {
    return { kind: "too-many-envelopes", count };
  }
  const sends: { fileIndex: number; device: RemoteDevice }[] = [];
  for (let fileIndex = 0; fileIndex < files.length; fileIndex += 1) {
    for (const device of devices) {
      sends.push({ fileIndex, device });
    }
  }
  return { kind: "ok", sends };
}
